import { Box } from '@gousto-internal/citrus-react'
import React from 'react'

import {
  CWCHero,
  CWCWhatsNewSection,
  CWCHomepageReviewsSection,
  CWCWeeklyMenu,
  CWCSuppliersSection,
  CWCHowItWorksSection,
  CWCWeeklyCollectionsSection,
  CWCFirstBoxSection,
  CWCFloatingCTA,
  DEFAULT_BOXES,
  type CWCWeeklyMenuProps,
} from '@library/landing-pages'

export type CWCHomepageProps = {
  onSignupCTAClick: () => void
  /** Sign-up destination the section CTAs link to. */
  signupUrl: string
  /**
   * Already-fetched menu data for the Weekly Menu section — fetched one layer up by
   * `HomePageContent`, not here. Undefined while that fetch is in flight, in which case the
   * section is simply omitted.
   */
  menu?: CWCWeeklyMenuProps['menu']
  /** Menu destination the Weekly Collections CTA links to. */
  menuUrl: string
  onMenuCTAClick: () => void
}

/**
 * Assembles the CwC homepage from the landed CWC sections in design order:
 * Hero → What's New → Reviews → Weekly Menu → Suppliers → How It Works → Weekly Collections →
 * First Box. Sections stack flush (each owns its own vertical padding), mirroring the existing
 * `Homepage`. First Box is the closing conversion moment before the footer, restating the hero's
 * offer with
 * one final sign-up CTA. This is the only place the CWC sections are composed.
 *
 * `CWCFloatingCTA` is a page-level sibling, not a section — it's a persistent fixed-position
 * overlay whose own visibility is scroll-derived (see its `useFloatingCTAVisibility` hook), not a
 * block in the page's normal flow, so its position in this JSX doesn't affect visual layout. It IS
 * composed right after Reviews (where its visible range begins), not last, because Tab order
 * follows DOM order regardless of scroll position: placed after First Box, a keyboard user tabbing
 * sequentially would reach First Box's own tab stops before ever reaching the floating CTA's DOM
 * position — by which point the visibility hook has already hidden it (`tabIndex={-1}`), making it
 * unreachable by keyboard for its entire visible window. Placed here, it's reachable via Tab as
 * soon as a keyboard user finishes Reviews, matching where the bar starts being visible.
 *
 * The CwC discount offer is fixed (`DEFAULT_BOXES`) and independent of the legacy homepage's
 * tapered-discount experiment — the CwC page is its own acquisition surface behind its own flag.
 * Hero and First Box are experiment-agnostic: they render whatever `discountBoxes` the assembly
 * supplies (per their specs, the caller resolves discount config, not the section).
 */
export function CWCHomepage({
  signupUrl,
  onSignupCTAClick,
  menu,
  menuUrl,
  onMenuCTAClick,
}: CWCHomepageProps) {
  return (
    <Box data-testid="cwcHomepage">
      <CWCHero discountBoxes={DEFAULT_BOXES} signupUrl={signupUrl} onCTAClick={onSignupCTAClick} />
      <CWCWhatsNewSection signupUrl={signupUrl} onCTAClick={onSignupCTAClick} />
      <CWCHomepageReviewsSection />
      <CWCFloatingCTA signupUrl={signupUrl} onCTAClick={onSignupCTAClick} />
      {menu && <CWCWeeklyMenu menu={menu} />}
      <CWCSuppliersSection />
      <CWCHowItWorksSection />
      <CWCWeeklyCollectionsSection href={menuUrl} onCTAClick={onMenuCTAClick} />
      <CWCFirstBoxSection discountBoxes={DEFAULT_BOXES} onCTAClick={onSignupCTAClick} />
    </Box>
  )
}
